import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ShieldCheck, FileText, Users, Download, ScrollText, Settings as SettingsIcon, History, Loader2, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const fmtDate = (s) => { try { return format(new Date(s), 'MMM d, yyyy HH:mm'); } catch { return s || '—'; } };

export default function AdminPrivacy() {
  return (
    <div className="space-y-5" data-testid="admin-privacy">
      <div>
        <div className="label-mini text-[#00C8FF]">Compliance</div>
        <h1 className="font-display font-black text-3xl mt-1 flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-[#00C8FF]" /> Privacy & Compliance</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage policies, consent, data requests and audit trail for GDPR / PDPA compliance.</p>
      </div>
      <Tabs defaultValue="policies">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="policies" data-testid="tab-policies"><FileText className="w-4 h-4 mr-1.5" /> Policies</TabsTrigger>
          <TabsTrigger value="consent" data-testid="tab-consent"><Users className="w-4 h-4 mr-1.5" /> Consent</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests"><Download className="w-4 h-4 mr-1.5" /> Data Requests</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-4 h-4 mr-1.5" /> Audit Log</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="w-4 h-4 mr-1.5" /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="policies" className="mt-4"><PoliciesTab /></TabsContent>
        <TabsContent value="consent" className="mt-4"><ConsentTab /></TabsContent>
        <TabsContent value="requests" className="mt-4"><RequestsTab /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function PoliciesTab() {
  const [policies, setPolicies] = useState(null);
  const [edit, setEdit] = useState(null);
  const [history, setHistory] = useState(null);
  const load = () => api.get('/admin/policies').then(r => setPolicies(r.data));
  useEffect(() => { load(); }, []);
  if (!policies) return <Loading />;
  return (
    <div className="space-y-3">
      {policies.map(p => (
        <div key={p.slug} className="regen-card-elevated p-4 flex items-center gap-3" data-testid={`policy-${p.slug}`}>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold flex items-center gap-2">{p.title}
              {p.is_core && <span className="pill bg-[#FF6FA8]/15 text-[#FF6FA8] text-[10px]">Re-consent required</span>}</div>
            <div className="text-[11px] text-muted-foreground">v{p.version} · updated {fmtDate(p.updated_at)} by {p.updated_by || 'system'}</div>
          </div>
          <Button size="sm" variant="outline" className="border-[var(--c-hair)]" onClick={() => setHistory(p.slug)} data-testid={`policy-history-${p.slug}`}><History className="w-3.5 h-3.5" /></Button>
          <Button size="sm" className="bg-[#00C8FF] text-[#081726] font-semibold" onClick={() => setEdit(p)} data-testid={`policy-edit-${p.slug}`}>Edit</Button>
        </div>
      ))}
      {edit && <EditPolicyDialog policy={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {history && <HistoryDialog slug={history} onClose={() => setHistory(null)} onRestored={() => { setHistory(null); load(); }} />}
    </div>
  );
}

function EditPolicyDialog({ policy, onClose, onSaved }) {
  const [title, setTitle] = useState(policy.title);
  const [body, setBody] = useState(policy.body.map(b => [...b]));
  const [notes, setNotes] = useState('');
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const setRow = (i, j, v) => setBody(b => b.map((row, ri) => ri === i ? row.map((c, ci) => ci === j ? v : c) : row));
  const addRow = () => setBody(b => [...b, ['', '']]);
  const delRow = (i) => setBody(b => b.filter((_, ri) => ri !== i));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/policies/${policy.slug}`, { title, body, change_notes: notes, publish_new_version: publish });
      toast.success(`Saved as v${r.data.version}${r.data.reconsent_required ? ' — users will re-accept on next login' : ''}`);
      onSaved();
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[88vh] overflow-y-auto max-w-2xl" data-testid="edit-policy-dialog">
        <DialogHeader><DialogTitle>Edit {policy.title} (v{policy.version})</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} className="bg-transparent border-[var(--c-hair)]" data-testid="edit-policy-title" /></div>
          {body.map((row, i) => (
            <div key={i} className="rounded-xl bg-[var(--c-film)] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={row[0]} onChange={e => setRow(i, 0, e.target.value)} placeholder="Heading" className="bg-transparent border-[var(--c-hair)] font-semibold" />
                <Button size="icon" variant="ghost" onClick={() => delRow(i)} className="text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></Button>
              </div>
              <textarea value={row[1]} onChange={e => setRow(i, 1, e.target.value)} rows={3} placeholder="Text" className="w-full rounded-md bg-transparent border border-[var(--c-hair)] px-3 py-2 text-sm" />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} className="border-[var(--c-hair)]"><Plus className="w-3.5 h-3.5 mr-1" /> Add section</Button>
          <div><Label>Change notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="What changed and why" className="bg-transparent border-[var(--c-hair)]" data-testid="edit-policy-notes" /></div>
          <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3">
            <div className="flex-1"><div className="text-sm font-medium">Publish as new version</div><div className="text-[11px] text-muted-foreground">Increments version{policy.is_core ? ' & forces all users to re-accept on next login' : ''}</div></div>
            <Switch checked={publish} onCheckedChange={setPublish} data-testid="edit-policy-publish" />
          </div>
          <Button onClick={save} disabled={busy} className="w-full bg-[#00C8FF] text-[#081726] font-semibold" data-testid="edit-policy-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ slug, onClose, onRestored }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/policies/${slug}/history`).then(r => setRows(r.data)); }, [slug]);
  const restore = async (version) => {
    try { await api.post(`/admin/policies/${slug}/restore?version=${version}`); toast.success(`Restored v${version}`); onRestored(); }
    catch { toast.error('Restore failed'); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[80vh] overflow-y-auto" data-testid="policy-history-dialog">
        <DialogHeader><DialogTitle>Version history — {slug}</DialogTitle></DialogHeader>
        {!rows ? <Loading /> : rows.length === 0 ? <Empty msg="No previous versions yet." /> : (
          <div className="space-y-2">
            {rows.map((h, i) => (
              <div key={i} className="rounded-xl bg-[var(--c-film)] p-3 flex items-center gap-3">
                <div className="flex-1"><div className="text-sm font-medium">v{h.version}</div><div className="text-[11px] text-muted-foreground">{h.change_notes || '—'} · archived {fmtDate(h.archived_at)}</div></div>
                <Button size="sm" variant="outline" className="border-[var(--c-hair)]" onClick={() => restore(h.version)} data-testid={`restore-${h.version}`}>Restore</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConsentTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/admin/privacy/consent-stats').then(r => setStats(r.data)); }, []);
  if (!stats) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total clients" value={stats.total_clients} color="#00C8FF" />
        <Stat label="Accepted latest" value={stats.accepted_latest} color='#65A30D' testid="stat-accepted" />
        <Stat label="Pending acceptance" value={stats.pending_count} color="#FF6FA8" testid="stat-pending" />
      </div>
      <div className="text-[11px] text-muted-foreground">Current core versions: {Object.entries(stats.current_versions).map(([k, v]) => `${k} v${v}`).join(' · ')}</div>
      <div className="regen-card-elevated overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--c-hair)] text-sm font-semibold">Clients pending acceptance</div>
        {stats.pending.length === 0 ? <Empty msg="Everyone is on the latest versions 🎉" /> : stats.pending.map(c => (
          <div key={c.id} className="px-4 py-3 border-b border-[var(--c-hair)] last:border-0 flex items-center gap-3" data-testid={`pending-${c.id}`}>
            <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{c.name}</div><div className="text-[11px] text-muted-foreground truncate">{c.email}</div></div>
            <div className="text-[11px] text-muted-foreground">{c.accepted_at ? `last v on ${fmtDate(c.accepted_at)}` : 'never accepted'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RequestsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/admin/privacy/data-requests').then(r => setRows(r.data)); }, []);
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty msg="No data export or deletion requests yet." />;
  return (
    <div className="regen-card-elevated overflow-hidden">
      {rows.map((r, i) => (
        <div key={i} className="px-4 py-3 border-b border-[var(--c-hair)] last:border-0 flex items-center gap-3" data-testid={`request-${i}`}>
          <span className={`pill text-[10px] ${r.type === 'deletion' ? 'bg-red-500/15 text-red-400' : 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]'}`}>{r.type}{r.format ? ` · ${r.format}` : ''}</span>
          <div className="flex-1 min-w-0 text-sm truncate">{r.email || r.user_ref || '—'}</div>
          <div className="text-[11px] text-muted-foreground">{r.status} · {fmtDate(r.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/admin/privacy/audit-log').then(r => setRows(r.data)); }, []);
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty msg="No policy changes recorded yet." />;
  return (
    <div className="regen-card-elevated overflow-hidden">
      {rows.map((a, i) => (
        <div key={i} className="px-4 py-3 border-b border-[var(--c-hair)] last:border-0" data-testid={`audit-${i}`}>
          <div className="text-sm"><span className="font-medium">{a.actor_name || 'admin'}</span> <span className="text-[#00C8FF]">{a.action}</span> {a.slug && <span className="text-muted-foreground">{a.slug} {a.version && `v${a.version}`}</span>}</div>
          <div className="text-[11px] text-muted-foreground">{a.notes ? `${a.notes} · ` : ''}{fmtDate(a.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/privacy/settings').then(r => setS(r.data)); }, []);
  if (!s) return <Loading />;
  const f = (k, v) => setS(p => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.put('/admin/privacy/settings', s); toast.success('Privacy settings saved'); }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const txt = (k, label, ph) => (<div><Label>{label}</Label><Input value={s[k] || ''} placeholder={ph} onChange={e => f(k, e.target.value)} className="bg-transparent border-[var(--c-hair)]" data-testid={`ps-${k}`} /></div>);
  return (
    <div className="regen-card-elevated p-5 space-y-3 max-w-2xl">
      {txt('retention_active', 'Retention — active accounts')}
      {txt('retention_after_delete', 'Retention — after deletion')}
      {txt('processors', 'Data processors')}
      {txt('contact_email', 'Privacy contact email')}
      {txt('company_name', 'Company name')}
      {txt('company_address', 'Company address')}
      <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3"><div className="flex-1 text-sm font-medium">Website essential cookies</div><Switch checked={!!s.cookie_essential} onCheckedChange={v => f('cookie_essential', v)} data-testid="ps-cookie-essential" /></div>
      <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3"><div className="flex-1 text-sm font-medium">Website analytics cookies</div><Switch checked={!!s.cookie_analytics} onCheckedChange={v => f('cookie_analytics', v)} data-testid="ps-cookie-analytics" /></div>
      <Button onClick={save} disabled={busy} className="w-full bg-[#00C8FF] text-[#081726] font-semibold" data-testid="ps-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}</Button>
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="regen-card-elevated p-4" data-testid={testid}>
      <div className="font-display font-black text-3xl" style={{ color }}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
const Loading = () => <div className="text-muted-foreground py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
const Empty = ({ msg }) => <div className="text-muted-foreground py-8 text-center text-sm">{msg}</div>;
